import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { BookOpenText, Sparkles, Trash2, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import { todayIso } from "@/lib/dates";

export const JobDiary = ({ jobId, title = "Site diary" }) => {
  const [entries, setEntries] = useState(null);
  const [date, setDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState("");

  const load = () => {
    api.get(`/jobs/${jobId}/diary`)
      .then((r) => setEntries(r.data.items || []))
      .catch(() => setEntries([]));
  };
  useEffect(load, [jobId]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/diary/generate`, { date });
      toast.success("Site diary written");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diary generation failed");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/diary/${id}`, { content: draft });
      setEditingId(null);
      load();
      toast.success("Diary updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this diary entry?")) return;
    try {
      await api.delete(`/diary/${id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section className="hard-card p-5" data-testid="job-diary-panel">
      <div className="label-tag mb-3 flex items-center gap-1.5">
        <BookOpenText className="w-3 h-3" /> {title}
        {entries && entries.length > 0 && (
          <span className="ml-1 bg-[#09090B] text-[#FF5F15] font-mono px-1.5 py-0.5 text-[9px]">{entries.length}</span>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <input
          type="date"
          value={date}
          max={todayIso()}
          onChange={(e) => setDate(e.target.value)}
          className="h-9 px-2 text-sm bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-[#FF5F15]"
          data-testid="diary-date-input"
        />
        <Button onClick={generate} disabled={busy}
                className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs h-9"
                data-testid="diary-generate-btn">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />
          {busy ? "Writing…" : "Generate activity summary"}
        </Button>
      </div>
      <p className="text-[11px] text-zinc-500 -mt-2 mb-4">
        One tap — AI writes the day's site log from timesheets, ticked tasks, photos and safety events. Review and edit before filing.
      </p>

      {!entries ? (
        <div className="text-xs text-zinc-500 font-mono">Loading…</div>
      ) : entries.length === 0 ? (
        <p className="text-xs text-zinc-500">No diary entries yet.</p>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <div key={e.id} className="border border-zinc-300 bg-zinc-50" data-testid={`diary-entry-${e.id}`}>
              <div className="flex items-center justify-between px-3 py-2 bg-[#09090B] text-white">
                <div className="text-xs font-bold uppercase tracking-wider">
                  {e.date}
                  {e.edited && <span className="ml-2 text-[9px] text-zinc-400 font-mono">edited</span>}
                </div>
                <div className="flex items-center gap-1">
                  {editingId === e.id ? (
                    <>
                      <button onClick={() => saveEdit(e.id)} className="p-1.5 text-[#39FF14] hover:bg-white/10" data-testid={`diary-save-${e.id}`}>
                        <Save className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="p-1.5 text-zinc-400 hover:bg-white/10">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => { setEditingId(e.id); setDraft(e.content); }} className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10" data-testid={`diary-edit-${e.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(e.id)} className="p-1.5 text-zinc-400 hover:text-red-400 hover:bg-white/10" data-testid={`diary-delete-${e.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {editingId === e.id ? (
                <Textarea value={draft} onChange={(ev) => setDraft(ev.target.value)} rows={12}
                          className="rounded-none border-0 text-sm font-mono" data-testid={`diary-textarea-${e.id}`} />
              ) : (
                <pre className="px-3 py-3 text-sm text-zinc-800 whitespace-pre-wrap font-sans leading-relaxed">{e.content}</pre>
              )}
              <div className="px-3 py-1.5 border-t border-zinc-200 text-[10px] font-mono text-zinc-500">
                {e.sources && `From ${e.sources.timesheets} timesheet(s) · ${e.sources.tasks} task(s) · ${e.sources.photos} photo(s)`}
                {" · "}by {e.generated_by_name}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
